import React from 'react';
import { TruckIcon } from '@heroicons/react/24/outline';
import type { QuoteCreatePayload } from '../fixtures';
import { MOVE_TYPES, SIZES, EQUIP_TYPES, RATE_ZONES, ACCOUNT_TYPES, WHSE_TYPES, PACKAGE_TYPES, WAREHOUSES, STORAGE_TYPES, DO_NOT_BILL_REASONS, NUM_UNITS, STORAGE_UNIT_TYPES, HANDLING_UNIT_TYPES, FUEL_TYPES_WAREHOUSE, LANE_EQUIP_TYPES } from '../quoteOptions';
import Row from '../../pro/components/orderform/Row';
import { Label } from '../../pro/components/orderform/Label';
import TextInput from '../../pro/components/orderform/TextInput';
import SelectInput from '../../pro/components/orderform/SelectInput';
import Icon from '../../pro/components/orderform/Icon';
import Card from './Card';
import LocationLookupField from './LocationLookupField';
import useQuoteDeliveryGeo from './useQuoteDeliveryGeo';

const LBL = 120;
const num = (v: string): number => Number(v) || 0;
/** Required-field styling, same as the Direct form: the INPUT turns red while
 *  blank and clears once filled; the label stays plain. */
const req = (v: string | number | null | undefined): 'red' | undefined =>
  (v == null || String(v).trim() === '' || v === 0) ? 'red' : undefined;

/** Which storage sub-fields apply per storagetype (V1 formatview_quote.pl:1426-1466;
 *  the backend's STORAGE_FIELD_TYPES guard mirrors it). Same table WarehouseTab uses. */
const STORAGE_ALLOWED: Record<string, string[]> = {
  storageanniversarydays: ['ANNIVERSARY'],
  storagefreedays: ['ANNIVERSARY', 'MANUAL'],
  storagercvmonthfree: ['ANNIVERSARY', 'SPLIT MONTH', 'MANUAL'],
  storagedonotbillreason: ['DO NOT BILL'],
};
export const storageAllowsOnCreate = (storagetype: string | undefined, field: keyof typeof STORAGE_ALLOWED) =>
  STORAGE_ALLOWED[field].includes((storagetype || '').toUpperCase());
/** Storage Rate is meaningless (and not required) on a DO NOT BILL account. */
export const storageRateApplies = (storagetype: string | undefined) =>
  (storagetype || '').toUpperCase() !== 'DO NOT BILL';

/** V1 defs_quote.pl required ('Y') transloadquote fields that the create drawer
 *  must collect, beyond Move Type / Size / Fuel Type which it already enforces.
 *  Returns the missing labels in display order. */
export function missingWarehouseCreateFields(t: NonNullable<QuoteCreatePayload['transload']>): string[] {
  const missing: string[] = [];
  if (!(t.warehouse ?? '').trim()) missing.push('Warehouse');
  if (!(t.whsetype ?? '').trim()) missing.push('Whse Type');
  if (!(t.accounttype ?? '').trim()) missing.push('Account Type');
  if (!(t.packagetype ?? '').trim()) missing.push('Package Type');
  if (!(t.commodity ?? '').trim()) missing.push('Commodity');
  if (!(t.ratezone ?? '').trim()) missing.push('Rate Zone');
  if (!(t.storagetype ?? '').trim()) missing.push('Storage Type');
  if (storageRateApplies(t.storagetype) && !(t.storagerate && t.storagerate > 0)) missing.push('Storage Rate');
  if (storageAllowsOnCreate(t.storagetype, 'storagefreedays') && t.storagefreedays == null) missing.push('Free Days');
  if (storageAllowsOnCreate(t.storagetype, 'storagedonotbillreason') && !(t.storagedonotbillreason ?? '').trim()) missing.push('Do Not Bill Reason');
  return missing;
}

interface StepTransloadProps {
  value: NonNullable<QuoteCreatePayload['transload']>;
  updateTransload: (patch: Partial<NonNullable<QuoteCreatePayload['transload']>>) => void;
  rateZoneOptions?: { value: string; label: string }[];
}

export const WarehouseMoveFields: React.FC<StepTransloadProps> = ({ value: t, updateTransload, rateZoneOptions }) => {
  const opts = (current: string | undefined, list: string[]) =>
    [current ?? '', ...list].filter((v, i, a) => v && a.indexOf(v) === i);
  return (
    <div>
      {/* Required set mirrors V1 defs_quote.pl 'Y' flags on transloadquote;
          blank required inputs show red, as on the Direct form. */}
      <Row><Label width={LBL}>Move Type</Label>
        <span id="cq-field-wmove" className="inline-flex">
          <SelectInput value={t.movetype ?? ''} onChange={(v) => updateTransload({ movetype: v })} options={MOVE_TYPES} ariaLabel="Warehouse Move Type" placeholder="Select..." highlight={req(t.movetype)} />
        </span></Row>
      <Row><Label width={LBL}>Warehouse</Label>
        <SelectInput value={t.warehouse ?? ''} onChange={(v) => updateTransload({ warehouse: v })} options={opts(t.warehouse, WAREHOUSES)} ariaLabel="Warehouse" placeholder="Select..." highlight={req(t.warehouse)} /></Row>
      <Row><Label width={LBL}>Whse Type</Label>
        <SelectInput value={t.whsetype ?? ''} onChange={(v) => updateTransload({ whsetype: v })} options={opts(t.whsetype, WHSE_TYPES)} ariaLabel="Whse Type" placeholder="Select..." highlight={req(t.whsetype)} /></Row>
      <Row><Label width={LBL}>Size / Type</Label>
        <span id="cq-field-wsize" className="inline-flex">
          <SelectInput value={t.size ?? ''} onChange={(v) => updateTransload({ size: v })} options={SIZES} width={80} ariaLabel="Warehouse Size" placeholder="Select..." highlight={req(t.size)} />
        </span>
        <SelectInput value={t.equiptype ?? ''} onChange={(v) => updateTransload({ equiptype: v })} options={opts(t.equiptype, EQUIP_TYPES)} placeholder="Select..." /></Row>
      <Row><Label width={LBL}>Package Type</Label>
        <SelectInput value={t.packagetype ?? ''} onChange={(v) => updateTransload({ packagetype: v })} options={opts(t.packagetype, PACKAGE_TYPES)} ariaLabel="Package Type" placeholder="Select..." highlight={req(t.packagetype)} /></Row>
      <Row><Label width={LBL}># Units</Label>
        <SelectInput value={t.numunits ?? ''} onChange={(v) => updateTransload({ numunits: v })} options={NUM_UNITS} width={96} placeholder="Select..." /></Row>
      <Row><Label width={LBL}>Commodity</Label>
        <TextInput value={t.commodity ?? ''} onChange={(v) => updateTransload({ commodity: v })} ariaLabel="Commodity" placeholder="—" highlight={req(t.commodity)} /></Row>
      <Row><Label width={LBL}>Rate Zone</Label>
        <SelectInput value={t.ratezone ?? ''} onChange={(v) => updateTransload({ ratezone: v })} options={rateZoneOptions?.length ? rateZoneOptions : opts(t.ratezone, RATE_ZONES)} ariaLabel="Warehouse Rate Zone" placeholder="Select..." highlight={req(t.ratezone)} /></Row>
    </div>
  );
};

export const WarehouseStorageDrayFields: React.FC<StepTransloadProps> = ({ value: t, updateTransload }) => {
  return (
    <div className="flex flex-col gap-2">
      {/* Account & Storage — same grouping as the saved quote's Warehouse tab.
          Sub-fields enable per storage type (V1 N16); DO NOT BILL swaps the
          rate for a required reason so a no-storage warehouse quote can be
          created here rather than only edited into shape afterwards. */}
      <Card compact collapsible={false} icon={<Icon.ticket className="h-3.5 w-3.5" />} title="Account & Storage">
        <Row><Label width={LBL}>Account Type</Label>
          <SelectInput value={t.accounttype ?? ''} onChange={(v) => updateTransload({ accounttype: v })} options={ACCOUNT_TYPES} width={140} ariaLabel="Account Type" placeholder="Select..." highlight={req(t.accounttype)} /></Row>
        <Row><Label width={LBL}>Storage Type</Label>
          <SelectInput value={t.storagetype ?? ''} onChange={(v) => updateTransload({
            storagetype: v,
            ...(!storageAllowsOnCreate(v, 'storagercvmonthfree') ? { storagercvmonthfree: 'N' } : {}),
          })} options={STORAGE_TYPES} ariaLabel="Storage Type" placeholder="Select..." highlight={req(t.storagetype)} />
          {storageAllowsOnCreate(t.storagetype, 'storageanniversarydays') && <>
            <Label># Anniv Days</Label>
            <TextInput value={t.storageanniversarydays != null ? String(t.storageanniversarydays) : ''} onChange={(v) => updateTransload({ storageanniversarydays: num(v) })} ariaLabel="Anniversary Days" width={56} placeholder="—" />
          </>}</Row>
        {storageAllowsOnCreate(t.storagetype, 'storagedonotbillreason') && (
          <Row><Label width={LBL}>Do Not Bill Reason</Label>
            <SelectInput value={t.storagedonotbillreason ?? ''} onChange={(v) => updateTransload({ storagedonotbillreason: v })} options={DO_NOT_BILL_REASONS} ariaLabel="Do Not Bill Reason" placeholder="Select..." highlight={req(t.storagedonotbillreason)} /></Row>
        )}
        {storageRateApplies(t.storagetype) && <>
          <Row><Label width={LBL}>Storage Rate</Label>
            <TextInput value={t.storagerate ? String(t.storagerate) : ''} onChange={(v) => updateTransload({ storagerate: num(v) })} width={120} ariaLabel="Storage Rate" placeholder="—" highlight={req(t.storagerate)} />
            <Label>Per</Label>
            <SelectInput value={t.storageunittype ?? ''} onChange={(v) => updateTransload({ storageunittype: v })} options={STORAGE_UNIT_TYPES} ariaLabel="Storage Unit Type" placeholder="Select..." /></Row>
          <Row><Label width={LBL}>Long-Term Rate</Label>
            <TextInput value={t.storageratelongterm ? String(t.storageratelongterm) : ''} onChange={(v) => updateTransload({ storageratelongterm: num(v) })} width={120} placeholder="—" /></Row>
        </>}
        {storageAllowsOnCreate(t.storagetype, 'storagercvmonthfree') && (
          <Row><Label width={LBL}>Receiving Month Free</Label>
            <SelectInput value={t.storagercvmonthfree ?? 'N'} onChange={(v) => updateTransload({ storagercvmonthfree: v })} options={['N', 'Y']} width={56} ariaLabel="Receiving Month Free" /></Row>
        )}
        {storageAllowsOnCreate(t.storagetype, 'storagefreedays') && (
          <Row><Label width={LBL}>Free Days</Label>
            {/* Free Days may legitimately be 0, so only a never-entered value is red. */}
            <TextInput value={t.storagefreedays != null ? String(t.storagefreedays) : ''} onChange={(v) => updateTransload({ storagefreedays: num(v) })} width={72} ariaLabel="Free Days" placeholder="—" highlight={t.storagefreedays == null ? 'red' : undefined} /></Row>
        )}
      </Card>
      <Card compact collapsible={false} icon={<TruckIcon className="h-3.5 w-3.5" />} title="Dray & Fuel">
        <Row><Label width={LBL}>Dray Rate</Label>
          {/* No Dray Type beside the rate: V1 retired transloadquote.draytype
              (defs_quote.pl:282 commented out) and new quotes leave it blank. */}
          <TextInput value={t.drayrate ? String(t.drayrate) : ''} onChange={(v) => updateTransload({ drayrate: num(v) })} width={120} ariaLabel="Warehouse Dray Rate" placeholder="—" /></Row>
        {/* Fuel sits directly under Dray Rate (CSR request); Handling follows. */}
        <Row><Label width={LBL}>Fuel</Label>
          <TextInput value={t.fuelsurcharge ? String(t.fuelsurcharge) : ''} onChange={(v) => updateTransload({ fuelsurcharge: num(v) })} width={96} ariaLabel="Warehouse Fuel" placeholder="—" />
          <SelectInput ariaLabel="Warehouse Fuel Type" value={t.fueltype ?? ''} onChange={(v) => updateTransload({ fueltype: v })} options={FUEL_TYPES_WAREHOUSE} placeholder="Select..." /></Row>
        <Row><Label width={LBL}>Handling</Label>
          <TextInput value={t.drayhandlingcharge ? String(t.drayhandlingcharge) : ''} onChange={(v) => updateTransload({ drayhandlingcharge: num(v) })} width={120} ariaLabel="Handling" placeholder="—" />
          {/* V1 "Per" (defs_quote.pl:288 drayhandlingunittype) — same list the Warehouse tab edits. */}
          <Label>Per</Label>
          <SelectInput ariaLabel="Handling Unit Type" value={t.drayhandlingunittype ?? ''} onChange={(v) => updateTransload({ drayhandlingunittype: v })} options={HANDLING_UNIT_TYPES} width={110} placeholder="Select..." />
          <Label>In Dray</Label>
          <SelectInput ariaLabel="In Dray" value={t.includehandlingindray ?? 'Y'} onChange={(v) => updateTransload({ includehandlingindray: v })} options={['N', 'Y']} width={56} /></Row>
      </Card>
    </div>
  );
};

interface StepLanesProps {
  lanes: NonNullable<QuoteCreatePayload['lanes']>;
  addLane: () => void;
  updateLane: (idx: number, patch: Partial<NonNullable<QuoteCreatePayload['lanes']>[number]>) => void;
  removeLane: (idx: number) => void;
  /** transloadquote.ratezone — the origin for each lane's mileage calc. */
  ratezone?: string;
}

type CreateLane = NonNullable<QuoteCreatePayload['lanes']>[number];

/** One create-time delivery lane. Own component so each lane carries its own
 *  geo hook: pick a cms.locations row to fill the address, leave the ZIP to
 *  fill city/state (or the reverse), and miles recompute from the quote's
 *  rate zone + the lane's LOCAL/OTR delivery type — the same V1
 *  getcitystatezip / calcmiles behaviour the Direct form and the saved
 *  quote's Delivery tab already have. */
const CreateLaneCard: React.FC<{
  lane: CreateLane; index: number; ratezone: string;
  update: (patch: Partial<CreateLane>) => void; remove: () => void;
}> = ({ lane, index, ratezone, update, remove }) => {
  const linked = (lane.deldelivertocode ?? 0) > 0;
  const geo = useQuoteDeliveryGeo({
    ratezone,
    deliveryzone: lane.delratezone ?? '',
    zip: lane.delzip ?? '',
    city: lane.delcity ?? '',
    state: lane.delstate ?? '',
    enabled: true,
    lockAddress: linked,
    onCityState: (city, state) => update({ delcity: city, delstate: state }),
    onZip: (zip) => update({ delzip: zip }),
    onMiles: (m) => update({ delmiles: num(m) }),
    miles: lane.delmiles ? String(lane.delmiles) : '',
  });
  const n = index + 1;
  return (
    <div className="border border-slate-200 dark:border-slate-700 rounded-md p-2.5 flex flex-col gap-1.5">
      <div className="flex items-center justify-between">
        <span className="text-[11px] font-semibold text-slate-500 dark:text-slate-400">Lane #{n}</span>
        <button type="button" aria-label={`Remove lane ${n}`} onClick={remove} className="text-[11px] text-red-600 hover:underline">Remove</button>
      </div>
      <Row><Label width={LBL}>Warehouse</Label><SelectInput value={lane.delwarehouse ?? ''} onChange={(v) => update({ delwarehouse: v })} options={[lane.delwarehouse ?? '', ...WAREHOUSES].filter((x, j, a) => x && a.indexOf(x) === j)} placeholder="Select..." /></Row>
      {/* V1 labels delratezone "Delivery Type" (LOCAL/OTR) and requires it. */}
      <Row><Label width={LBL}>Delivery Type</Label>
        <SelectInput value={lane.delratezone ?? ''} onChange={(v) => update({ delratezone: v })} options={['LOCAL', 'OTR']} width={96} ariaLabel={`Lane ${n} Delivery Type`} placeholder="Select..." highlight={req(lane.delratezone)} /></Row>
      <Row><Label width={LBL}>Deliver To</Label>
        <LocationLookupField
          value={lane.deldelivertoname ?? ''}
          linkedCode={lane.deldelivertocode ?? 0}
          onType={(v) => update({ deldelivertoname: v, deldelivertocode: 0 })}
          onPick={(row) => update({
            deldelivertoname: row.lname, deldelivertocode: row.locationcode,
            deladdr1: row.address1, deladdr2: row.address2,
            delcity: row.city, delstate: row.state, delzip: row.zip,
          })}
          ariaLabel={`Lane ${n} Deliver To`}
          placeholder="—"
        /></Row>
      <Row><Label width={LBL}>City / St / Zip</Label>
        <TextInput value={lane.delcity ?? ''} onChange={(v) => update({ delcity: v })} onBlur={geo.handleCityStateBlur} readOnly={linked} ariaLabel={`Lane ${n} City`} placeholder="City" />
        <TextInput value={lane.delstate ?? ''} onChange={(v) => update({ delstate: v })} onBlur={geo.handleCityStateBlur} readOnly={linked} ariaLabel={`Lane ${n} State`} width={48} placeholder="ST" />
        <TextInput value={lane.delzip ?? ''} onChange={(v) => update({ delzip: v })} onBlur={geo.handleZipBlur} readOnly={linked} ariaLabel={`Lane ${n} Zip`} width={80} placeholder="ZIP" /></Row>
      <Row><Label width={LBL}>Equip / Miles</Label>
        <SelectInput value={lane.delequiptype ?? ''} onChange={(v) => update({ delequiptype: v })} options={[lane.delequiptype ?? '', ...LANE_EQUIP_TYPES].filter((x, j, a) => x && a.indexOf(x) === j)} placeholder="Select..." />
        <TextInput value={lane.delmiles ? String(lane.delmiles) : ''} onChange={(v) => update({ delmiles: num(v) })} width={80} ariaLabel={`Lane ${n} Miles`} placeholder="mi" />
        {geo.geoBusy && <span className="text-[0.6875rem] text-slate-400 dark:text-slate-500">Calculating…</span>}</Row>
      {geo.milesError && (
        <div className="text-[0.6875rem] text-amber-600 dark:text-amber-400" style={{ paddingLeft: LBL }}>{geo.milesError}</div>
      )}
      <Row><Label width={LBL}>Deliv Rate</Label>
        <TextInput value={lane.deldeliveryrate ? String(lane.deldeliveryrate) : ''} onChange={(v) => update({ deldeliveryrate: num(v) })} width={120} placeholder="—" /></Row>
    </div>
  );
};

export const WarehouseLaneFields: React.FC<StepLanesProps> = ({ lanes, addLane, updateLane, removeLane, ratezone = '' }) => {
  return (
    <div className="flex flex-col gap-2">
      <div className="flex justify-end">
        <button type="button" onClick={addLane} className="text-[11.5px] font-semibold text-blue-600 hover:underline">+ Add Lane</button>
      </div>
      {lanes.length === 0 ? (
        <div className="px-3 py-6 text-center text-slate-400 text-[12px] bg-slate-50 dark:bg-slate-800 border border-dashed border-slate-200 dark:border-slate-700 rounded-md">
          No lanes yet — optional. Add delivery lanes here, or create the quote and add them on the Delivery tab.
        </div>
      ) : (
        <div className="flex flex-col gap-2">
          {lanes.map((lane, i) => (
            <CreateLaneCard key={i} lane={lane} index={i} ratezone={ratezone}
              update={(patch) => updateLane(i, patch)} remove={() => removeLane(i)} />
          ))}
        </div>
      )}
    </div>
  );
};
